import { lotDescriptor } from '../descriptors/lot.descriptor.js';
import { resolveProductStockControl, productDeclaresVariation } from '../services/productStockControl.js';
import { isPartialVariant } from '../services/productVariantService.js';
import { MAX_QUANTITY } from '../validation/fieldLimits.js';
import { mergeErrors } from './mergeErrors.js';
import { max, positive, required } from './rules.js';
import { runDescriptorValidation } from './runDescriptorValidation.js';
import { validateWithRules } from './validateWithRules.js';
/**
 * L0 + L1 validation for Lot.
 *
 * Pass via `ctx.extra`:
 * - `siblings` — existing lots (unique lotNumber per product)
 * - `product` — Product record for variation + stock-control checks
 * - `variant` — ProductVariant when `variantId` is set (must be partial — 0106/0107)
 *
 * Org stock params: `ctx.orgParams.stock` (same as product stock cascade).
 */
export function validateLot(entity, ctx) {
    return mergeErrors(runDescriptorValidation(lotDescriptor, entity, [
        'lotNumber',
        'productId',
        'variantId',
        'notes',
        'plannedQuantity',
    ]), validateWithRules(entity, {
        lotNumber: [required('Informe o número do lote.')],
        productId: [required('Selecione o produto.')],
        plannedQuantity: [
            positive('Informe uma quantidade maior que zero.'),
            max(MAX_QUANTITY, 'Valor acima do máximo permitido (999.999.999.999,999).'),
        ],
    }, ctx), uniqueLotNumberError(entity, ctx), dateOrderError(entity), variantIdentityError(entity, ctx), partialVariantError(entity, ctx), stockControlError(entity, ctx));
}
function uniqueLotNumberError(entity, ctx) {
    const siblings = ctx?.extra?.['siblings'];
    const lotNumber = entity.lotNumber?.trim();
    const productId = entity.productId?.trim();
    if (!siblings || !lotNumber || !productId)
        return undefined;
    const excludeId = ctx?.extra?.['excludeId'] ?? entity.id;
    const conflict = siblings.find((s) => s.productId === productId &&
        (s.lotNumber ?? '').trim() === lotNumber &&
        (!excludeId || s.id !== excludeId));
    if (!conflict)
        return undefined;
    return { lotNumber: 'Já existe um lote com este número para o produto.' };
}
function dateOrderError(entity) {
    const production = entity.productionDate;
    const expiry = entity.expiryDate;
    if (!production || !expiry)
        return undefined;
    const p = production instanceof Date ? production : new Date(production);
    const e = expiry instanceof Date ? expiry : new Date(expiry);
    if (Number.isNaN(p.getTime()) || Number.isNaN(e.getTime()))
        return undefined;
    if (e.getTime() < p.getTime()) {
        return { expiryDate: 'A validade não pode ser anterior à fabricação.' };
    }
    return undefined;
}
function variantIdentityError(entity, ctx) {
    const product = ctx?.extra?.['product'];
    if (!product)
        return undefined;
    const declares = productDeclaresVariation(product);
    const variantId = entity.variantId?.trim() || null;
    if (declares && !variantId) {
        return { variantId: 'Informe a variante (o produto declara variação).' };
    }
    if (!declares && variantId) {
        return { variantId: 'Variante não permitida: o produto não declara variação.' };
    }
    return undefined;
}
/** Lot may only reference a partial (identity) variant — never a sized/full one (0107). */
function partialVariantError(entity, ctx) {
    const variantId = entity.variantId?.trim() || null;
    if (!variantId)
        return undefined;
    const variant = ctx?.extra?.['variant'];
    if (!variant)
        return undefined;
    if (variant.id !== variantId)
        return undefined;
    if (!isPartialVariant(variant)) {
        return {
            variantId: 'Lote deve referenciar variante parcial (identidade), sem tamanho. Use a variante completa só no razão.',
        };
    }
    return undefined;
}
function stockControlError(entity, ctx) {
    const product = ctx?.extra?.['product'];
    if (!product)
        return undefined;
    const orgStock = ctx?.orgParams?.stock ?? null;
    const resolved = resolveProductStockControl(product, orgStock);
    if (!resolved.values.stockControlByLot) {
        return {
            productId: resolved.reasons.stockControlByLot ??
                'O produto não tem controle de lote efetivo.',
        };
    }
    if (resolved.values.stockHasExpiry && !entity.expiryDate) {
        return { expiryDate: 'Informe a validade (o produto exige controle de validade).' };
    }
    return undefined;
}
//# sourceMappingURL=validateLot.js.map